'use client'

import { useState, useEffect } from 'react'
import Link from '@/components/ui/Link'
import Badge from '@/components/ui/Badge'
import CriteriaOverview, { type CriteriaOverviewProps } from '@/components/ui/CriteriaOverview'
import RecommendationBadge from '@/components/ui/RecommendationBadge'
import { Icon } from '@/icons/Icon'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import {
  Recommendation,
  Classification,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { queryPayload } from '@/lib/payloadClient'
import type { App, Category, Review } from '@/payload-types'

const logger = createLogger('CategoryOverview')

type CategoryOverviewProps = CriteriaOverviewProps & {
  reviewId: Review['id']
  categories: Array<
    Category & {
      recommendation: Recommendation
      classification: Classification
    }
  >
  reviewCriterionRatings?: CriteriaOverviewProps['reviewCriterionRatings']
}

type CategoryOverviewLineItemProps = {
  app: Pick<App, 'id' | 'name' | 'slug' | 'icon' | 'brandBackgroundColor' | 'brandPrimaryColor'>
  reviewId: Review['id']
  category: Category & {
    recommendation: Recommendation
    classification: Classification
  }
  reviewCriterionRatings?: CriteriaOverviewProps['reviewCriterionRatings']
}

function CategoryOverviewLineItem({
  category,
  reviewId,
  app,
  reviewCriterionRatings,
}: CategoryOverviewLineItemProps) {
  const [efficiencyPoints, setEfficiencyPoints] = useState({ points: 0, total: 0 })
  const shouldShowOverview = efficiencyPoints.points > 0 ? category.id : null

  useEffect(() => {
    let isMounted = true

    async function fetchAndSetEfficiencyPoints() {
      if (!reviewId) return
      try {
        const { docs: reviewCategoryCriterionRatingDocs } = await queryPayload({
          collection: 'review_categoryCriterion_rating',
          select: {
            rating: true,
            isApplicable: true,
          },
          where: {
            review: { equals: reviewId },
            'categoryCriterion.category': { equals: category.id },
          },
          depth: 0,
          pagination: false,
          cacheKey: `query:rcr:eff:${reviewId}:${category.id}`,
        })

        const applicableDocs = reviewCategoryCriterionRatingDocs.filter(
          (doc) => doc?.isApplicable !== false,
        )

        const total = applicableDocs.length
        const points = applicableDocs.reduce((acc, doc) => {
          if (doc?.rating && doc.rating >= 5) {
            return acc + 1
          }
          return acc
        }, 0)

        if (isMounted) {
          setEfficiencyPoints({ points, total })
        }
      } catch (error) {
        logger.error('Failed to fetch efficiency points:', error)
      }
    }

    fetchAndSetEfficiencyPoints()

    return () => {
      isMounted = false
    }
  }, [reviewId, category.id])

  return (
    <div
      key={category.id}
      className="group relative flex h-16 cursor-pointer items-center justify-between gap-5 bg-gray-50 px-5 transition-colors first:rounded-t-xl last:rounded-b-xl hover:bg-gray-100"
    >
      <div className="flex items-center gap-3">
        <Icon
          name={category.icon!}
          className="text-gray-500 transition-colors duration-300 group-hover:text-primary-500"
        />
        <span className="truncate font-medium text-gray-950">{category.name}</span>
        {category.classification === Classification.Primary && (
          <Badge style="subtle" className="hidden gap-[3px] sm:flex" nested>
            Main <span className="hidden lg:inline-flex">Category</span>
          </Badge>
        )}

        <CriteriaOverview
          app={app}
          categoryId={category.id}
          reviewCriterionRatings={reviewCriterionRatings}
          horizontalAlign="right"
          verticalAlign="center"
          hideDescription={true}
          disabled={!shouldShowOverview}
        >
          <div
            onClick={(event) => {
              if (efficiencyPoints.points <= 0) return
              event.preventDefault()
            }}
            className={cn(
              'relative z-30 mt-0.5 shrink-0 text-12 text-gray-500',
              efficiencyPoints.points > 0
                ? 'cursor-pointer hover:underline hover:underline-offset-2'
                : '',
            )}
          >
            {efficiencyPoints.total > 0 &&
              `${efficiencyPoints.points} of ${efficiencyPoints.total} points`}
          </div>
        </CriteriaOverview>
      </div>
      <RecommendationBadge
        recommendation={category.recommendation ?? Recommendation.NotRecommended}
        className="group-hover:-translate-x-7"
        efficiencyPoints={efficiencyPoints}
        category={category}
      />

      <Link
        href={`/best/${category.slug}`}
        aria-label={`View the best apps in ${category.name}`}
        className="absolute left-0 top-0 z-20 h-full w-full"
      ></Link>

      <div
        aria-hidden="true"
        className="absolute right-5 top-1/2 z-10 -translate-y-1/2 rotate-180 text-gray-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      >
        <Icon name="chevron-left" size={12} />
      </div>
    </div>
  )
}

export default function CategoryOverview({
  app,
  categories,
  reviewId,
  reviewCriterionRatings,
}: CategoryOverviewProps) {
  return (
    <div className="divide-y divide-gray-200 rounded-xl border border-gray-200">
      {categories.map((category) => {
        return (
          <CategoryOverviewLineItem
            key={category.id}
            app={app}
            category={category}
            reviewId={reviewId}
            reviewCriterionRatings={reviewCriterionRatings}
          />
        )
      })}
    </div>
  )
}
